import { Injectable, OnDestroy, inject } from "@angular/core";
import { flatten, isNil, uniq } from "lodash";
import { BehaviorSubject, Observable, Subject, forkJoin } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ApiClientService } from "~app/common/services/api-client.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VkKartaFastigheterPopupComponent } from "~app/features/virkeskop/karta/vk-karta-fastigheter-popup/vk-karta-fastigheter-popup.component";
import { IMapComponent } from "../../../map.models";
import { IMapGeometry, IMapGeometryWicket, IMapSettings } from "../../../map.settings";
import { IMapQueryResult, MapQueryService } from "../../../services/map-query.service";
import { SpatialService } from "../../../services/spatial.service";
import { AvtalFastigheterStore } from "./avtal-fastigheter.store";
import { Fastighet, FastighetGeometry, PersonOrganisationsnummerStatusDto } from "./map-fastighet-models";

@Injectable()
export class MapFastighetService implements OnDestroy {
    readonly idDrawlayer = "fastigheter";
    readonly idFeaturelayer = "MetriaFastighetsgranser";

    guid: string;
    hasSelection: boolean;
    isEnabled: boolean;
    searchByMapClickEnabled: boolean;
    map: IMapComponent;
    mapsettings: IMapSettings;
    selectFastighetCallback: (event) => void;
    private readonly apiClientService = inject(ApiClientService);
    private readonly destroy$ = new Subject<void>();
    private readonly fastighetSubLayerId = 0;
    private readonly fastigheterStore = inject(AvtalFastigheterStore);
    private readonly importSrc = new BehaviorSubject<FastighetGeometry>(null);
    private readonly mapQueryService = inject(MapQueryService);
    private readonly spatialService = inject(SpatialService);

    private importEnabled = false;

    constructor() {
        this.guid = GuidUtils.newGuid();
    }

    get fastigheter$() { return this.fastigheterStore.fastigheter$; }

    get import$() { return this.importSrc.asObservable(); }

    addFastighet(fastighet: FastighetGeometry): void {
        this.fastigheterStore.add(fastighet);
    }

    disableKarta() {
        this.isEnabled = false;
        this.searchByMapClickEnabled = false;
        this.map?.clearGeometries(this.idDrawlayer);
        this.map?.hideDrawLayer(this.idDrawlayer);
        this.map?.clearClass();
        this.hasSelection = false;
    }

    enableKarta() {
        this.isEnabled = true;
        this.map?.showDrawLayer(this.idDrawlayer);
    }

    enableSearchByMapClick() {
        this.searchByMapClickEnabled = true;
    }

    getFastighet(uuid: string, allowCache: boolean): Observable<Fastighet> {
        return this.apiClientService.get<Fastighet>(`fastighet/${uuid}?allowCache=${allowCache}`);
    }

    getIsLeverantor(orgnr: string[]): Observable<PersonOrganisationsnummerStatusDto[]> {
        return this.apiClientService.get<PersonOrganisationsnummerStatusDto[]>(`fastighet/leverantorer/orgnr`, false, { orgnr: orgnr });
    }

    hasCached(uuid: string): Observable<boolean> {
        return this.apiClientService.get(`fastighet/${uuid}/exists`);
    }

    importFastighet(fastighet: FastighetGeometry): void {
        this.importSrc.next(fastighet);
    }

    init(mapref: IMapComponent) {
        this.map = mapref;
    }

    kommunSearch(input): Observable<any[]> {
        return this.apiClientService.get<any[]>("TypeAhead/Kommun", false, { query: input });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openPopupOnGeometry(mouse, geometry: IMapGeometry, canImportAvtal: boolean) {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }
        if (isNil(geometry)) {
            return;
        }
        this.map.showPopupOnGeometry(VkKartaFastigheterPopupComponent, geometry, {
            geometry: geometry,
            importEnabled: canImportAvtal
        }).pipe(
            takeUntil(this.destroy$)
        ).subscribe(_popupRef => {
            const uuid = geometry?.attributes?.objekt_id;
            this.hasCached(uuid).subscribe(hasCached => {
                if (hasCached) {
                    this.getFastighet(uuid, true).subscribe();
                }
            });
        });
    }

    preinit(mapsettings: IMapSettings, arcView: any) {
        this.mapsettings = mapsettings;
        this.mapQueryService.init(mapsettings, arcView);
        this.setupPrereqs();
    }

    searchByHitGeometry(mouse, geometry) {
        this.mapQueryService.queryLayer({
            layerid: this.idFeaturelayer,
            arcGeometry: mouse.point,
            sublayerqueries: [{ layerid: this.fastighetSubLayerId }]
        }).subscribe(queryResponse => {
            if (this.isEnabled) {
                this.queryByQueryResultUniqueObjekt(queryResponse).pipe(
                    takeUntil(this.destroy$)
                ).subscribe(fastigheter => {
                    this.renderAndOpenIfSingle(mouse, fastigheter);
                });
            }
        });
    }

    searchByKommunBeteckning(kommun: string, fastighetsbeteckning: string) {
        this.mapQueryService.queryLayer({
            layerid: this.idFeaturelayer,
            sublayerqueries: [
                {
                    layerid: this.fastighetSubLayerId,
                    params: [
                        {
                            name: "kommunnamn",
                            value: kommun?.toUpperCase()
                        },
                        {
                            name: "fastighet",
                            value: fastighetsbeteckning.toUpperCase(),
                            isLike: true
                        }
                    ]
                }
            ]
        }).subscribe(queryResponse => {
            this.queryByQueryResultUniqueObjekt(queryResponse).pipe(
                takeUntil(this.destroy$)
            ).subscribe(fastigheter => {
                this.renderAndOpenIfSingle(null, fastigheter);
            });
        });
    }

    setImportEnabled(enabled: boolean) {
        this.importEnabled = enabled;
    }

    setSelectFastighet(callback: (event) => void) {
        this.selectFastighetCallback = callback;
    }

    setupPrereqs() {
        let layer = this.mapsettings.featurelayers.find(fl => fl.id === this.idFeaturelayer);
        if (layer === undefined) {
            layer = {
                id: this.idFeaturelayer,
                visible: true
            };
            this.mapsettings.featurelayers.push(layer);
        }
        if (ObjectUtils.isNullOrUndefined(layer.onClick)) {
            layer.onClick = (mouse, geometry) => {
                if (this.isEnabled && this.searchByMapClickEnabled) {
                    this.searchByHitGeometry(mouse, geometry);
                }
                return false;
            };
        }
    }

    private copyMetadata(from: IMapGeometry, to: IMapGeometry): any {
        to.attributes = {};
        return Object.assign(to.attributes, from.attributes);
    }

    private copyMetadataFromFirst(from: IMapGeometry[], to: IMapGeometry): void {
        to.attributes = from?.length > 0 ? this.copyMetadata(from[0], to) : to.attributes;
    }

    private queryByQueryResultUniqueObjekt(queryResponse: IMapQueryResult[]) {
        return this.queryLayerByObjektId(
            uniq(
                flatten(
                    queryResponse.map(x => x.geometries.filter(g => ObjectUtils.hasValue(g.attributes.objekt_id)).map(g => g.attributes.objekt_id as string)))));
    }

    private queryLayerByObjektId(objektId: string[]): Observable<IMapGeometry[]> {
        const result$ = new Subject<IMapGeometry[]>();
        const fastighetsGeometrier: IMapGeometryWicket[] = [];

        const queries = objektId.map(oid =>
            this.mapQueryService.queryLayer(
                {
                    layerid: this.idFeaturelayer,
                    sublayerqueries: [
                        {
                            layerid: this.fastighetSubLayerId,
                            params: [
                                {
                                    name: "objekt_id",
                                    value: oid.toLowerCase(),
                                    isLike: false
                                }
                            ]
                        }
                    ]
                })
        );
        forkJoin(queries).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: results => {
                results.forEach(r => {
                    const allFlatGeometries = flatten(r.map(r2 => r2.geometries));
                    const joinedMultipolygon = this.spatialService.join(allFlatGeometries, true);
                    this.copyMetadataFromFirst(allFlatGeometries, joinedMultipolygon);
                    fastighetsGeometrier.push(joinedMultipolygon);
                });
                result$.next(fastighetsGeometrier);
            },
            error: (e) => result$.error(e)
        });
        return result$;
    }

    private renderAndOpenIfSingle(mouse, fastigheter: IMapGeometry[]) {
        this.renderFastighetsGeometries(fastigheter);

        if (fastigheter?.length === 1) {
            const oneGeometry = fastigheter[0];
            this.openPopupOnGeometry(mouse, oneGeometry, this.importEnabled);
        }
    }

    private renderFastighetsGeometries(response: IMapGeometry[]) {
        if (ObjectUtils.isNullOrUndefined(response) || this.isEnabled === false) {
            return;
        }
        this.hasSelection = response?.length > 0;
        this.map?.showDrawLayer(this.idDrawlayer);
        response.forEach(g => {
            g.visible = true;
            g.label = `${g.attributes?.trakt} ${g.attributes?.blockenhet}`;
            g.size = 2;
            g.color = [
                0,
                0,
                255,
                0.3
            ];
            g.selected = false;
            g.id = g.attributes.objekt_id;
        });
        this.map?.setGeometries(this.idDrawlayer, response, true);
    }
}
